import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, NavigationEnd, ActivatedRouteSnapshot } from '@angular/router';
import { NavigationService, ProviderService } from '@app/core/services';
import { AuthService } from '@app/core/services/auth.service';
import { ClinicalEncounterService } from '@app/core/services/clinical-encounter.service';
import { PatientsService } from '@app/core/services/patients.service';
import { ActivePatient, SidebarLocations } from '@app/core/services/state/ui-state/ui-state.store';
import { UiStateService } from '@app/core/services/ui-state.service';
import { PatientVo } from 'api-clinician-app';
import * as _ from 'lodash';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  mapTo,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  filter,
  withLatestFrom,
} from 'rxjs/operators';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AudioRecorderService } from '@app/modules/sidebar/speech-to-notes/services/audio-recorder.service';
import { VITALS_ONLY_PERMISSION } from '../../services/role-manager.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  // animations: [slideInOutAnimation]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  VITALS_ONLY_PERMISSION = VITALS_ONLY_PERMISSION;

  searchControl = new FormControl();
  options: string[] = ['One', 'Two', 'Three'];
  filteredPatients$: Observable<PatientVo[]>;

  authenticated$ = this.authService.authenticated$.pipe(distinctUntilChanged(_.isEqual), untilDestroyed(this));
  superAdmin$ = this.authService.superAdmin$.pipe(distinctUntilChanged(_.isEqual), untilDestroyed(this));
  currentTenantId$ = this.authService.currentTenantId$.pipe(distinctUntilChanged(_.isEqual), untilDestroyed(this));
  currentPatientId$ = this.uiStateService.currentPatientId$.pipe(distinctUntilChanged(_.isEqual), untilDestroyed(this));
  currentRoutedPatient$: Observable<string> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null),
    map(() => this.getParamFromTree(this.route.snapshot, 'patientId')),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
    untilDestroyed(this)
  );

  displayProgressBar$ = this.uiStateService.displayProgressBar$.pipe(distinctUntilChanged(_.isEqual), untilDestroyed(this));
  activePatients$: Observable<ActivePatient[]> = this.uiStateService.activePatients$.pipe(
    distinctUntilChanged(_.isEqual),
    untilDestroyed(this)
  );
  showSearch = false;

  patients$ = this.patientsService.patients$.pipe(distinctUntilChanged(_.isEqual), untilDestroyed(this));
  currentProvider$ = this.providerService.provider$.pipe(distinctUntilChanged(_.isEqual), untilDestroyed(this));

  constructor(
    private fb: FormBuilder,
    private navService: NavigationService,
    private authService: AuthService,
    private patientsService: PatientsService,
    private providerService: ProviderService,
    private uiStateService: UiStateService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private snackbar: MatSnackBar,
    private clinicalEncounterService: ClinicalEncounterService,
    private audioRecorderService: AudioRecorderService
  ) {}

  ngOnInit() {
    this.filteredPatients$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map(value => value.toLowerCase()),
      distinctUntilChanged(),
      debounceTime(250),
      switchMap(value => this.searchPatients(value)),
      untilDestroyed(this)
    );

  }

  private getParamFromTree(route: ActivatedRouteSnapshot, paramName: string): string | null {
    if (route.paramMap.has(paramName)) {
      return route.paramMap.get(paramName);
    }

    for (const child of route.children) {
      const param = this.getParamFromTree(child, paramName);
      if (param) {
        return param;
      }
    }

    return null;
  }

  isRecordingAndNotPaused$(patientId: string) {
    return this.audioRecorderService.recordingStatusFor(patientId).pipe(map(status => status === 'recording'));
  }

  isRecordingAndPaused$(patientId: string) {
    return this.audioRecorderService.recordingStatusFor(patientId).pipe(map(status => status === 'paused'));
  }

  inProgressEncounter(tenantId, patientId) {
    return this.clinicalEncounterService.getPatientInProgressEncounterId(patientId) != null;
  }

  searchPatients(searchStr: string) {
    if (searchStr.length >= 3) {
      return this.patientsService.searchPatientsForCurrentPractice$(searchStr);
    } else {
      return of([] as PatientVo[]);
    }
  }

  ngOnDestroy(): void {}

  clearSearch() {
    this.searchControl.setValue('');
    this.searchInput.nativeElement.blur();
  }

  focusSearchInput() {
    this.searchInput.nativeElement.focus();
  }

  selectPatient(patientId: string) {
    this.currentPatientId$
      .pipe(
        take(1),
        switchMap(currentPatientId => {
          return this.audioRecorderService.recordingStatusFor(currentPatientId).pipe(take(1));
        }),
        tap(recordingStatus => {
          if (recordingStatus !== 'idle') {
            this.audioRecorderService.pauseRecording();
            this.snackbar.open('Recording paused', 'OK', { duration: 5000 });
          }
        }),
        withLatestFrom(this.currentTenantId$),
        tap(([recordingStatus, tenantId]) => {
          if (patientId) {
            this.navService.navigate([tenantId, 'patient', patientId]);
          } else {
            this.navService.navigate([tenantId, 'home']);
          }
          this.clearSearch();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  selectPatientTab(patientId: string) {
    this.currentPatientId$
      .pipe(
        take(1),
        switchMap(currentPatientId => {
          return this.audioRecorderService.recordingStatusFor(currentPatientId).pipe(take(1));
        }),
        tap(recordingStatus => {
          if (recordingStatus !== 'idle') {
            this.audioRecorderService.pauseRecording();
            this.snackbar.open('Recording paused', 'OK', { duration: 5000 });
          }
        }),
        withLatestFrom(this.currentTenantId$.pipe(take(1))),
        tap(([recordingStatus, tenantId]) => {
          this.navService.navigate([tenantId, 'patient', patientId]);
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  autocompleteDisplay(patient: PatientVo) {
    if (patient && typeof patient === 'object') {
      return `${patient.PatientDetails.FirstName} ${patient.PatientDetails.Surname}`;
    }
  }

  logout() {
    this.audioRecorderService.guardedLogout$(this.dialog).pipe(untilDestroyed(this)).subscribe();
  }

  removeActivePatient(tenantId: string, patientId: string) {
    this.audioRecorderService
      .confirmClosePatientTab$(this.dialog, patientId)
      .pipe(
        filter(result => result !== 'cancel'),
        withLatestFrom(this.currentPatientId$),
        tap(([result, currentPatientId]) => {
          if (result === 'navigate_to_recording') {
            const recordingPatientId = this.audioRecorderService.getPatientId();
            if (recordingPatientId) {
              this.navService.navigate([tenantId, 'patient', recordingPatientId, 'consult']);
              this.uiStateService.navigateSidebar(SidebarLocations.NORA_CONSULT_SUMMARY);
            }
            return;
          }
          if (currentPatientId === patientId) {
            this.navService.navigate([tenantId, 'home']);
          }
          this.uiStateService.removeActivePatient(patientId);
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  addToWaitingRoom(patientId: string, tenantId: string) {
    this.providerService.waitingRoom$
      .pipe(
        take(1),
        map(waitingRoomPatients =>
          waitingRoomPatients.find(
            waitingRoomPatient => waitingRoomPatient.PatientId === patientId && waitingRoomPatient.PatientEvent.PracticeId === tenantId
          )
        ),
        switchMap(waitingRoomPatient =>
          !!waitingRoomPatient
            ? of(waitingRoomPatient).pipe(mapTo(false))
            : this.patientsService.loadPatientInfo(tenantId, patientId).pipe(
                take(1),
                switchMap(patient => this.providerService.addPatientToWaitingRoom(tenantId, patient).pipe(debounceTime(1000), mapTo(true)))
              )
        ),
        tap(added =>
          added
            ? this.snackbar.open('Patient added to waiting  room', 'OK', { duration: 5000 })
            : this.snackbar.open('Patient already in the waiting room', 'OK', { duration: 5000 })
        ),
        tap(() => this.autocomplete.closePanel()),
        untilDestroyed(this)
      )
      .subscribe();
  }
}

function notNullAndLowercaseIncludes(searchStr: string, pField: string) {
  return searchStr && pField && pField.toLocaleLowerCase().includes(searchStr.toLocaleLowerCase());
}
